import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Pencil } from "lucide-react";
import { api, thumbUrl, formatApiError } from "@/lib/api";

export default function AdminFeatured() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/items", { params: { featured: true } }).then((r) => r.data),
      api.get("/admin/categories").then((r) => r.data),
    ])
      .then(([its, cats]) => {
        setItems(its);
        setCategories(cats);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const unfeature = async (item) => {
    try {
      await api.put(`/admin/items/${item.id}`, { ...item, featured: false });
      toast.success("Removed from featured");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl text-charcoal-900 mb-1">Featured Work</h1>
      <p className="text-sm text-charcoal-500 mb-8">Featured published work can be highlighted on the homepage and portfolio.</p>

      {loading ? (
        <p className="text-sm text-charcoal-500 font-mono">Loading...</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-charcoal-900/20 bg-surface-white/60 p-10 text-center" data-testid="featured-empty">
          <Star className="w-7 h-7 mx-auto text-copper-600/60" />
          <p className="mt-3 font-serif text-xl">Nothing featured yet</p>
          <p className="text-sm text-charcoal-500 mt-1">Star any work item from All Work to feature it here.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-list">
          {items.map((item, i) => {
            const cover = item.media?.find((m) => m.media_type === "image") || item.media?.[0];
            return (
              <div key={item.id} className="bg-surface-white border border-charcoal-900/10 rounded-2xl overflow-hidden" data-testid={`featured-row-${i}`}>
                <div className="aspect-[16/9] bg-surface-muted overflow-hidden">
                  {cover?.media_type === "image" && (
                    <img src={thumbUrl(cover)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  )}
                </div>
                <div className="p-5">
                  <p className="font-semibold text-sm text-charcoal-900">{item.title}</p>
                  <p className="text-xs text-charcoal-500 mt-0.5">{catMap[item.category_id]?.name || "Uncategorized"}</p>
                  <div className="mt-4 flex gap-2">
                    <button
                      data-testid={`featured-remove-${i}`}
                      onClick={() => unfeature(item)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-charcoal-900/20 text-xs font-medium px-4 py-2 hover:border-copper-600 hover:text-copper-600 transition-colors"
                    >
                      <Star className="w-3.5 h-3.5" /> Unfeature
                    </button>
                    <Link
                      to={`/admin/work/${item.id}/edit`}
                      data-testid={`featured-edit-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-charcoal-900/20 text-xs font-medium px-4 py-2 hover:border-copper-600 hover:text-copper-600 transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
